// Category metadata used by the frontend for filter configs and category-specific
// attribute rendering. Slugs align with the backend seed_data.py.

export const CATEGORIES = [
  { slug: "cars", label: "Cars", tagline: "Slightly used, mostly still running." },
  { slug: "houses", label: "Houses", tagline: "Roof included at no extra charge." },
  { slug: "boats", label: "Boats & Yachts", tagline: "For water enthusiasts, generally." },
  { slug: "flights", label: "Flights", tagline: "One-way to somewhere better than here." },
  { slug: "hotels", label: "Hotels", tagline: "Sleep somewhere expensive." },
  { slug: "electronics", label: "Electronics", tagline: "Screens for people who like screens." },
  { slug: "home-kitchen", label: "Home & Kitchen", tagline: "Objects for your countertops." },
  { slug: "clothing", label: "Clothing & Fashion", tagline: "Fabric shaped like clothes." },
  { slug: "beauty", label: "Beauty & Personal Care", tagline: "Bottles with big promises." },
  { slug: "toys", label: "Toys & Games", tagline: "For small humans and their guardians." },
  { slug: "sports", label: "Sports & Outdoors", tagline: "Reasons to leave the house." },
  { slug: "books", label: "Books", tagline: "Long-form written entertainment." },
  { slug: "groceries", label: "Groceries", tagline: "Edible items in various states." },
  { slug: "pet", label: "Pet Supplies", tagline: "For the animals in your life." },
  { slug: "office", label: "Office Supplies", tagline: "Productivity, allegedly." },
  { slug: "household", label: "Household Essentials", tagline: "Toilet paper, milk, and bread — in terrifying quantities." },
  { slug: "specials", label: "Special Products", tagline: "Weird little things we couldn't fit anywhere else." },
];

export const CATEGORY_LABEL = Object.fromEntries(
  CATEGORIES.map((c) => [c.slug, c.label])
);

// Which attribute keys to prominently show on cards + detail per category.
export const CATEGORY_ATTR_HIGHLIGHTS = {
  cars: ["year", "mileage", "bodyType", "fuel", "transmission"],
  houses: ["bedrooms", "bathrooms", "sqft", "city", "style"],
  boats: ["lengthFt", "boatType", "hullMaterial", "year"],
  flights: ["origin", "destination", "departureDate", "cabinClass", "durationHours"],
  hotels: ["location", "starRating", "pricePerNight", "nights"],
  electronics: ["deviceType", "brand", "warrantyYears", "color"],
  "home-kitchen": ["brand", "room", "material", "color"],
  clothing: ["brand", "garmentType", "size", "gender", "color", "material"],
  beauty: ["brand", "category", "volumeMl", "skinType"],
  toys: ["brand", "toyType", "ageRange", "players"],
  sports: ["brand", "sport", "size", "gender"],
  books: ["author", "genre", "format", "pages"],
  groceries: ["brand", "category", "weightG", "unitCount", "organic"],
  pet: ["brand", "petType", "productType", "size"],
  office: ["brand", "productType", "quantityPerPack", "color"],
  household: ["brand", "category", "unitCount", "bulkTier"],
  specials: ["flavor", "size", "packSize", "heat", "sweetness"],
};

export const ATTR_LABELS = {
  year: "Year",
  mileage: "Mileage",
  bodyType: "Body",
  fuel: "Fuel",
  transmission: "Transmission",
  bedrooms: "Bedrooms",
  bathrooms: "Bathrooms",
  sqft: "Sqft",
  city: "City",
  style: "Style",
  lengthFt: "Length (ft)",
  boatType: "Type",
  hullMaterial: "Hull",
  origin: "From",
  destination: "To",
  departureDate: "Depart",
  cabinClass: "Cabin",
  durationHours: "Duration (h)",
  location: "Location",
  starRating: "Stars",
  pricePerNight: "$ / night",
  nights: "Nights",
  amenities: "Amenities",
  deviceType: "Device",
  brand: "Brand",
  warrantyYears: "Warranty (yrs)",
  color: "Color",
  room: "Room",
  material: "Material",
  garmentType: "Garment",
  size: "Size",
  gender: "Gender",
  category: "Category",
  volumeMl: "Volume (ml)",
  skinType: "Skin type",
  toyType: "Toy type",
  ageRange: "Age range",
  players: "Players",
  sport: "Sport",
  author: "Author",
  genre: "Genre",
  format: "Format",
  pages: "Pages",
  weightG: "Weight (g)",
  unitCount: "Units",
  organic: "Organic",
  petType: "Pet",
  productType: "Type",
  quantityPerPack: "Qty / pack",
  bulkTier: "Bulk tier",
  flavor: "Flavor",
  packSize: "Pack size",
  servingTemp: "Serve at",
  heat: "Heat",
  sweetness: "Sweetness",
  vegan: "Vegan",
  glutenFree: "Gluten-free",
  caffeine: "Caffeine",
  pricePerCan: "Price / can",
  savingsVsSingle: "Savings",
};

export const formatAttrValue = (key, value) => {
  if (value === undefined || value === null) return "—";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (key === "mileage") return `${value.toLocaleString()} mi`;
  if (key === "sqft") return `${value.toLocaleString()} sqft`;
  if (key === "pricePerNight") return `$${Number(value).toLocaleString()}`;
  return String(value);
};

export const formatMoney = (n) => {
  const v = Number(n);
  // Use decimal precision for small-ticket items (e.g., $2.99); round for large.
  if (!Number.isFinite(v)) return "$0";
  if (v < 100 && Math.round(v) !== v) {
    return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

export const formatMoneyDecimal = (n) =>
  `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
